import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/auth';
import api from '../services/api'; 
import { useNavigate } from 'react-router-dom'; // 💡 NUEVA IMPORTACIÓN

// --- NUEVA VARIABLE GLOBAL Y FUNCIÓN PARA SINCRONIZACIÓN ---
// Permite al Interceptor de Axios notificar al Context
let setTokenRefreshCounter: React.Dispatch<React.SetStateAction<number>> | null = null;

export const triggerAuthRefresh = () => {
    if (setTokenRefreshCounter) {
        setTokenRefreshCounter(c => c + 1);
    }
};
// ------------------------------------------------

// Basic user shape aligned with backend users/serializers.py UserSerializer
export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  is_active?: boolean;
}

type RegisterInput = {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  re_password: string; // UI input; will be mapped to password2 for backend
  phone_number?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
};

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterInput) => Promise<void>;
  logout: () => void;
  setUser: (u: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  
  // 💡 LLAMADA AL HOOK DE REDIRECCIÓN
  const navigate = useNavigate(); 
  
  // --- ESTADO NUEVO PARA FORZAR RE-EJECUCIÓN (SINCRONIZACIÓN) ---
  const [refreshCounter, setRefreshCounter] = useState(0);

  const isAuthenticated = useMemo(() => !!localStorage.getItem('access_token'), [user]);

  // Hook para exponer el setter al interceptor
  useEffect(() => {
      setTokenRefreshCounter = setRefreshCounter;
      return () => { setTokenRefreshCounter = null; }; 
  }, []); 

  // Hydrate on mount from tokens/localStorage Y en cada refresh
  // El cambio en [refreshCounter] fuerza la re-validación
  useEffect(() => {
    const init = async () => {
      if (!localStorage.getItem('access_token')) {
          setLoading(false);
          return;
      }

      try {
        // 1. Si tenemos un usuario en caché, usarlo inmediatamente para UI snappy
        const cached = localStorage.getItem('user');
        if (cached) {
          try {
            setUser(JSON.parse(cached));
          } catch {}
        }
        
        // 2. Fetch del perfil para validación autoritativa (usa el token actual)
        const me = await authService.getCurrentUser();
        setUser(me);
        localStorage.setItem('user', JSON.stringify(me));
      } catch (e) {
        // 3. Si falla (ej: 401), el token es inválido. Limpiamos y dejamos como invitado.
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [refreshCounter]); // <-- DEPENDENCIA CLAVE PARA SINCRONIZACIÓN

  const mergeCartIfAny = useCallback(async () => {
    try {
      const sessionKey = localStorage.getItem('cart_session_key');
      if (!sessionKey) return;
      await api.post('orders/cart/merge/', { session_key: sessionKey });
    } catch {
      // Non-blocking; ignore failures
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await authService.login(email, password);
    // Fetch profile
    const me = await authService.getCurrentUser();
    setUser(me);
    localStorage.setItem('user', JSON.stringify(me));
    await mergeCartIfAny();
  }, [mergeCartIfAny]);

  const register = useCallback(async (data: RegisterInput) => {
    // Map UI re_password -> password2 expected by backend
    const { re_password, ...rest } = data;
    await authService.register({ ...rest, password2: re_password });
    // Optional: auto-login after registration for smoother UX
    await login(data.email, data.password);
  }, [login]);

  // 💡 FUNCIÓN LOGOUT MODIFICADA
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    navigate('/'); // <-- REDIRIGE A LA PÁGINA DE INICIO
  }, [navigate]);

  const value = useMemo(() => ({ user, isAuthenticated, loading, login, register, logout, setUser }), [user, isAuthenticated, loading, login, register, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};